import { useState, useEffect, useRef, useCallback } from 'react';

const WS_URL = 'wss://mempool.space/api/v1/ws';
const BLOCKS_URL = 'https://mempool.space/api/v1/blocks';
const FEES_URL = 'https://mempool.space/api/v1/fees/recommended';
const DIFFICULTY_URL = 'https://mempool.space/api/v1/difficulty-adjustment';
const MEMPOOL_URL = 'https://2140data.com/api/mempool';
const BLOCKS_FALLBACK_URL = 'https://2140data.com/api/v1/blocks';
const WS_RECONNECT_DELAY = 5000;

export const useMempoolBlock = () => {
  const [block, setBlock] = useState(null);
  const [blockHistory, setBlockHistory] = useState([]);
  const [fees, setFees] = useState(null);
  const [difficulty, setDifficulty] = useState(null);
  const [mempoolInfo, setMempoolInfo] = useState(null);
  const [mempoolTxCount, setMempoolTxCount] = useState(null);
  const [mempoolBlocks, setMempoolBlocks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewBlock, setIsNewBlock] = useState(false);
  const [vBytesPerSecond, setVBytesPerSecond] = useState(null);
  const [trackedTxData, setTrackedTxData] = useState(null);
  const [liveBlock0TxCount, setLiveBlock0TxCount] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const abortRef = useRef(null);
  const feesAbortRef = useRef(null);
  const diffAbortRef = useRef(null);
  const mempoolAbortRef = useRef(null);
  const blocksFallbackAbortRef = useRef(null);
  const mountedRef = useRef(true);
  const lastHeightRef = useRef(null);
  const initialLoadRef = useRef(true);
  const trackedTxidRef = useRef(null);
  const trackingMempoolBlockRef = useRef(false);
  const liveBlock0SetRef = useRef(null);
  const keepAliveTimerRef = useRef(null);
  const healthCheckTimerRef = useRef(null);
  const lastDataTimeRef = useRef(Date.now());
  const reconnectAttemptsRef = useRef(0);

  const parseBlock = (raw) => ({
    height: raw.height,
    hash: raw.id,
    timestamp: raw.timestamp,
    txCount: raw.tx_count,
    size: raw.size,
    weight: raw.weight,
    difficulty: raw.difficulty,
    pool: raw.extras?.pool?.name || 'Unknown',
    totalFees: raw.extras?.totalFees,
    medianFee: raw.extras?.medianFee,
    feeRange: raw.extras?.feeRange,
    reward: raw.extras?.reward,
  });

  const handleNewBlock = useCallback((parsed) => {
    if (!mountedRef.current) return;
    const isNew = !initialLoadRef.current && lastHeightRef.current !== null && parsed.height > lastHeightRef.current;
    initialLoadRef.current = false;

    // Only update the "current" block if it's >= what we have
    if (lastHeightRef.current === null || parsed.height >= lastHeightRef.current) {
      lastHeightRef.current = parsed.height;
      setBlock(parsed);
    }
    setBlockHistory(prev => {
      const map = new Map();
      prev.forEach(b => map.set(b.height, b));
      map.set(parsed.height, parsed);
      const sorted = [...map.values()].sort((a, b) => b.height - a.height);

      // Only keep contiguous blocks from the top (no gaps)
      const contiguous = [sorted[0]];
      for (let i = 1; i < sorted.length; i++) {
        if (contiguous[contiguous.length - 1].height - sorted[i].height > 1) break;
        contiguous.push(sorted[i]);
      }
      return contiguous.slice(0, 16);
    });
    setLoading(false);
    if (isNew) {
      setIsNewBlock(true);
      setTimeout(() => { if (mountedRef.current) setIsNewBlock(false); }, 4000);
    }
  }, []);

  const mergeBlockHistory = useCallback((incoming) => {
    setBlockHistory(prev => {
      const incomingTop = incoming.length > 0 ? incoming[0].height : 0;
      const prevTop = prev.length > 0 ? prev[0].height : 0;

      // If incoming batch is fresher, replace entirely
      if (incomingTop >= prevTop) return incoming.slice(0, 16);

      // If existing is fresher, only merge blocks that are contiguous with the top
      const map = new Map();
      prev.forEach(b => map.set(b.height, b));
      incoming.forEach(b => map.set(b.height, b));
      const sorted = [...map.values()].sort((a, b) => b.height - a.height);

      // Remove blocks that create gaps (height difference > 1 between consecutive)
      const contiguous = [sorted[0]];
      for (let i = 1; i < sorted.length; i++) {
        if (contiguous[contiguous.length - 1].height - sorted[i].height > 1) break;
        contiguous.push(sorted[i]);
      }
      return contiguous.slice(0, 16);
    });
  }, []);

  const fetchBlocks = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(BLOCKS_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) throw new Error('Primary blocks fetch failed');
      const blocks = await res.json();
      if (blocks.length > 0) {
        handleNewBlock(parseBlock(blocks[0]));
        mergeBlockHistory(blocks.slice(0, 16).map(parseBlock));
        return true;
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Mempool blocks fetch failed:', err.message);
    }
    return false;
  }, [handleNewBlock, mergeBlockHistory]);

  const fetchFees = useCallback(async () => {
    if (feesAbortRef.current) feesAbortRef.current.abort();
    const controller = new AbortController();
    feesAbortRef.current = controller;
    try {
      const res = await fetch(FEES_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) return;
      const json = await res.json();
      setFees(json);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Fee estimate fetch failed:', err.message);
    }
  }, []);

  const fetchDifficulty = useCallback(async () => {
    if (diffAbortRef.current) diffAbortRef.current.abort();
    const controller = new AbortController();
    diffAbortRef.current = controller;
    try {
      const res = await fetch(DIFFICULTY_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) return;
      const json = await res.json();
      setDifficulty(json);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Difficulty fetch failed:', err.message);
    }
  }, []);

  const fetchMempoolTxCount = useCallback(async () => {
    if (mempoolAbortRef.current) mempoolAbortRef.current.abort();
    const controller = new AbortController();
    mempoolAbortRef.current = controller;
    try {
      const res = await fetch(MEMPOOL_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) return;
      const json = await res.json();
      if (json.count != null) setMempoolTxCount(json.count);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('Mempool TX count fetch failed:', err.message);
    }
  }, []);

  const fetchBlocksFallback = useCallback(async () => {
    if (blocksFallbackAbortRef.current) blocksFallbackAbortRef.current.abort();
    const controller = new AbortController();
    blocksFallbackAbortRef.current = controller;
    try {
      const res = await fetch(BLOCKS_FALLBACK_URL, { signal: controller.signal });
      if (!res.ok || !mountedRef.current) return;
      const blocks = await res.json();
      if (blocks.length > 0) {
        handleNewBlock(parseBlock(blocks[0]));
        mergeBlockHistory(blocks.slice(0, 16).map(parseBlock));
      }
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('2140data blocks fetch failed:', err.message);
    }
  }, [handleNewBlock, mergeBlockHistory]);

  const connectWs = useCallback(() => {
    if (!mountedRef.current) return;
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    if (keepAliveTimerRef.current) { clearInterval(keepAliveTimerRef.current); keepAliveTimerRef.current = null; }

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      lastDataTimeRef.current = Date.now();
      ws.send(JSON.stringify({ action: 'want', data: ['blocks', 'stats', 'mempool-blocks'] }));
      if (trackedTxidRef.current) {
        ws.send(JSON.stringify({ 'track-tx': trackedTxidRef.current }));
      }
      if (trackingMempoolBlockRef.current) {
        liveBlock0SetRef.current = null;
        ws.send(JSON.stringify({ 'track-mempool-block': 0 }));
      }
      // Keep-alive ping: mempool.space drops idle TCP connections without firing onclose.
      keepAliveTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try { ws.send('ping'); } catch (e) {}
        }
      }, 21000);
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;
      lastDataTimeRef.current = Date.now();
      reconnectAttemptsRef.current = 0;
      try {
        const data = JSON.parse(event.data);
        if (data.block) handleNewBlock(parseBlock(data.block));
        if (data.mempoolInfo) {
          setMempoolInfo({
            size: data.mempoolInfo.size,
            vsize: data.mempoolInfo.vsize,
            count: data.mempoolInfo.count || data.mempoolInfo.size,
          });
        }
        if (Array.isArray(data['mempool-blocks'])) {
          setMempoolBlocks(data['mempool-blocks']);
        }
        if (data.fees) setFees(data.fees);
        if (data.da) setDifficulty(data.da);
        if (data.vBytesPerSecond != null) setVBytesPerSecond(data.vBytesPerSecond);
        if (data.txPosition) {
          const txid = Array.isArray(data.txPosition.txid)
            ? data.txPosition.txid[0]
            : data.txPosition.txid;
          if (txid && txid === trackedTxidRef.current) {
            setTrackedTxData({
              txid,
              position: data.txPosition.position,
              cpfp: data.txPosition.cpfp,
            });
          }
        }
        if (data['projected-block-transactions']) {
          const pbt = data['projected-block-transactions'];
          if (pbt.index === 0) {
            if (!liveBlock0SetRef.current) liveBlock0SetRef.current = new Set();
            const set = liveBlock0SetRef.current;
            if (Array.isArray(pbt.transactions)) {
              set.clear();
              pbt.transactions.forEach(t => {
                const txid = Array.isArray(t) ? t[0] : t.txid;
                if (txid) set.add(txid);
              });
            }
            if (pbt.delta) {
              (pbt.delta.added || []).forEach(t => {
                const txid = Array.isArray(t) ? t[0] : t.txid;
                if (txid) set.add(txid);
              });
              (pbt.delta.removed || []).forEach(txid => {
                if (typeof txid === 'string') set.delete(txid);
              });
            }
            setLiveBlock0TxCount(set.size);
          }
        }
      } catch (err) {

      }
    };

    ws.onerror = () => {};
    ws.onclose = () => {
      // If a newer connect already replaced us, do nothing — avoids clearing the new conn's keepalive.
      if (wsRef.current !== ws) return;
      if (keepAliveTimerRef.current) { clearInterval(keepAliveTimerRef.current); keepAliveTimerRef.current = null; }
      wsRef.current = null;
      if (!mountedRef.current) return;
      // Exponential backoff capped at 30s: 5s, 10s, 20s, 30s, 30s...
      reconnectAttemptsRef.current += 1;
      const delay = Math.min(30000, WS_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current - 1));
      reconnectTimerRef.current = setTimeout(connectWs, delay);
    };
  }, [handleNewBlock]);

  useEffect(() => {
    mountedRef.current = true;

    const fetchBlocksWithFallback = async () => {
      const ok = await fetchBlocks();
      if (!ok) fetchBlocksFallback();
    };

    fetchBlocksWithFallback();
    fetchFees();
    fetchDifficulty();
    fetchMempoolTxCount();
    connectWs();

    const refreshAll = () => {
      fetchBlocksWithFallback();
      fetchMempoolTxCount();
      connectWs();
    };

    const handleOnline = () => {
      console.group('Mempool reconnecting');
      console.log('Internet restored');
      console.groupEnd();
      refreshAll();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Mempool: tab visible, refreshing data');
        refreshAll();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Stall detector: if no WS message for 60s, the connection is a zombie. Force reconnect.
    healthCheckTimerRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      if (Date.now() - lastDataTimeRef.current <= 60000) return;
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        connectWs();
      } else {
        try { wsRef.current.send('ping'); } catch (e) { connectWs(); }
      }
    }, 10000);

    return () => {
      mountedRef.current = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleVisibilityChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (keepAliveTimerRef.current) clearInterval(keepAliveTimerRef.current);
      if (healthCheckTimerRef.current) clearInterval(healthCheckTimerRef.current);
      if (abortRef.current) abortRef.current.abort();
      if (feesAbortRef.current) feesAbortRef.current.abort();
      if (diffAbortRef.current) diffAbortRef.current.abort();
      if (blocksFallbackAbortRef.current) blocksFallbackAbortRef.current.abort();
      if (mempoolAbortRef.current) mempoolAbortRef.current.abort();
    };
  }, [connectWs, fetchBlocks, fetchBlocksFallback, fetchFees, fetchDifficulty, fetchMempoolTxCount]);

  const trackTx = useCallback((txid) => {
    trackedTxidRef.current = txid || null;
    setTrackedTxData(null);
    if (txid && wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ 'track-tx': txid }));
      } catch (e) {}
    }
  }, []);

  const trackMempoolBlock = useCallback((index = 0) => {
    if (trackingMempoolBlockRef.current) return;
    trackingMempoolBlockRef.current = true;
    liveBlock0SetRef.current = null;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ 'track-mempool-block': index }));
      } catch (e) {}
    }
  }, []);

  return {
    block, blockHistory, fees, difficulty, mempoolInfo, mempoolTxCount, mempoolBlocks, loading, isNewBlock,
    vBytesPerSecond, trackedTxData, liveBlock0TxCount,
    trackTx, trackMempoolBlock,
  };
};
